import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const billingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listInvoices: build.query({
      query: (filter) => `/invoices${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      // ALWAYS provide the LIST tag (not only when empty) — otherwise `invalidatesTags: LIST`
      // from create/delete mutations never re-fetches a non-empty list.
      providesTags: (result) => [
        { type: 'Invoice', id: 'LIST' },
        ...(result?.items?.map((i) => ({ type: 'Invoice', id: i.id })) ?? []),
      ],
    }),
    getInvoice: build.query({
      query: (id) => `/invoices/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Invoice', id }],
    }),
    createInvoice: build.mutation({
      query: (body) => ({ url: '/invoices', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id: 'NEXT_NUMBER' }, 'Dashboard'],
    }),
    // Manual billing-register entry — server auto-generates the next THC/YY-YY/N number.
    createManualBilling: build.mutation({
      query: (body) => ({ url: '/invoices/manual', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id: 'NEXT_NUMBER' }, 'Dashboard'],
    }),
    addPayment: build.mutation({
      query: ({ id, ...body }) => ({ url: `/invoices/${id}/payments`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      // Also busts the Receipts register (a receipt created here shows up there too — same Payments table).
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Invoice', id }, { type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id: 'RECEIPTS_LIST' }, 'Dashboard'],
    }),
    // "Clear Dues" — receipt-only settlement across a patient's outstanding invoices (no new bill).
    settleDues: build.mutation({
      query: (body) => ({ url: '/invoices/settle-dues', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Invoice', id: 'LIST' }, 'Dashboard', { type: 'Patient', id: 'LIST' }],
    }),
    // Inline billing-register edit: cost/session, sessions, discount, amount paid.
    updateInvoiceBilling: build.mutation({
      query: ({ id, ...body }) => ({ url: `/invoices/${id}/billing`, method: 'PATCH', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Invoice', id }, { type: 'Invoice', id: 'LIST' }, 'Dashboard'],
    }),
    // Inline billing-register edit: change the (single) receipt's payment method.
    updateInvoicePaymentMethod: build.mutation({
      query: ({ id, method }) => ({ url: `/invoices/${id}/payment-method`, method: 'PATCH', body: { method } }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Invoice', id }, { type: 'Invoice', id: 'LIST' }, 'Dashboard'],
    }),
    // Admin-only: delete a bill, reversing any unconsumed session packages it funded.
    // Also frees its invoice number for reuse (CodeGenerator scans non-deleted invoices only).
    deleteInvoice: build.mutation({
      query: (id) => ({ url: `/invoices/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Invoice', id }, { type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id: 'NEXT_NUMBER' }, 'Dashboard'],
    }),
    // Reconciliation totals over the same filter set as the register list.
    invoiceTotals: build.query({
      query: (filter) => `/invoices/totals${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Invoice', id: 'LIST' }],
    }),
    // Preview-only: the THC/YY-YY/N number the next bill would get. Reserves nothing.
    // Tagged NEXT_NUMBER so create/delete mutations bust this cache — otherwise a second bill
    // opened in the same session (without a page refresh) would prefill the number just used
    // by the first, and the server would reject it as already occupied.
    peekNextInvoiceNumber: build.query({
      query: (issuedAt) => `/invoices/next-number${toQuery({ issuedAt })}`,
      transformResponse: (resp) => resp.data?.invoiceNumber,
      providesTags: [{ type: 'Invoice', id: 'NEXT_NUMBER' }],
    }),
    // Exact-match lookup — used to warn the operator live if a typed/edited number is already taken.
    findInvoiceByNumber: build.query({
      query: (number) => `/invoices/by-number${toQuery({ number })}`,
      transformResponse: (resp) => resp.data,
    }),
    // Streams the invoice PDF as a Blob. responseHandler keeps fetchBaseQuery from trying to JSON-parse
    // the binary; keepUnusedDataFor:0 avoids caching multi-MB blobs in the RTK store. Errors (404/403)
    // surface via the standard error path (check err.status — err.data is a Blob here, not JSON).
    downloadInvoice: build.query({
      query: (invoiceId) => ({
        url: `/billing/invoices/${invoiceId}/download`,
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
      keepUnusedDataFor: 0,
    }),
    listDues: build.query({
      // Optional issue-date range filter (DueAt is rarely populated, so dates run on IssuedAt).
      query: ({ from, to } = {}) => {
        const p = new URLSearchParams();
        if (from) p.append('from', from);
        if (to) p.append('to', to);
        const s = p.toString();
        return `/billing/dues${s ? `?${s}` : ''}`;
      },
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Invoice', id: 'DUES' }, { type: 'Invoice', id: 'LIST' }],
    }),
    revenue: build.query({
      query: ({ from, to }) => {
        const p = new URLSearchParams();
        if (from) p.append('from', from);
        if (to) p.append('to', to);
        return `/billing/revenue?${p.toString()}`;
      },
      transformResponse: (resp) => resp.data,
    }),
    billingSummary: build.query({
      query: ({ from, to, excludeHomeopathy }) => {
        const p = new URLSearchParams();
        if (from) p.append('from', from);
        if (to) p.append('to', to);
        if (excludeHomeopathy) p.append('excludeHomeopathy', 'true');
        return `/billing/summary?${p.toString()}`;
      },
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Invoice', id: 'LIST' }],
    }),
    // Billing → Receipts register: every Payment row across all invoices, flattened with invoice/patient info.
    listReceipts: build.query({
      query: (filter) => `/invoices/receipts${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: (result) => [
        { type: 'Invoice', id: 'RECEIPTS_LIST' },
        ...(result?.items?.map((r) => ({ type: 'Invoice', id: `RECEIPT_${r.id}` })) ?? []),
      ],
    }),
    createReceipt: build.mutation({
      query: (body) => ({ url: '/invoices/receipts', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, body) => [
        { type: 'Invoice', id: 'RECEIPTS_LIST' }, { type: 'Invoice', id: 'LIST' },
        { type: 'Invoice', id: body.invoiceId }, 'Dashboard',
      ],
    }),
    // Edit date/amount/method/receipt no./notes on an existing receipt (Admin-only).
    updateReceipt: build.mutation({
      query: ({ id, ...body }) => ({ url: `/invoices/receipts/${id}`, method: 'PATCH', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Invoice', id: `RECEIPT_${id}` }, { type: 'Invoice', id: 'RECEIPTS_LIST' }, { type: 'Invoice', id: 'LIST' }, 'Dashboard',
      ],
    }),
    // Delete a receipt (Admin-only) — reverses its amount out of the invoice's AmountPaid.
    deleteReceipt: build.mutation({
      query: (id) => ({ url: `/invoices/receipts/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Invoice', id: `RECEIPT_${id}` }, { type: 'Invoice', id: 'RECEIPTS_LIST' }, { type: 'Invoice', id: 'LIST' }, 'Dashboard',
      ],
    }),
    // Admin-only: one-shot data fix for invoices whose line items never credited a SessionPackage.
    backfillSessionPackages: build.mutation({
      query: () => ({ url: '/invoices/backfill-session-packages', method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Invoice', id: 'LIST' }, 'Dashboard'],
    }),
  }),
});

export const {
  useListInvoicesQuery, useLazyListInvoicesQuery, useGetInvoiceQuery, useCreateInvoiceMutation,
  useCreateManualBillingMutation,
  useAddPaymentMutation, useUpdateInvoiceBillingMutation, useUpdateInvoicePaymentMethodMutation, useSettleDuesMutation,
  useDeleteInvoiceMutation,
  useLazyDownloadInvoiceQuery,
  useListDuesQuery, useRevenueQuery, useBillingSummaryQuery,
  useInvoiceTotalsQuery, usePeekNextInvoiceNumberQuery, useLazyFindInvoiceByNumberQuery,
  useBackfillSessionPackagesMutation,
  useListReceiptsQuery, useLazyListReceiptsQuery,
  useCreateReceiptMutation, useUpdateReceiptMutation, useDeleteReceiptMutation,
} = billingApi;
